import { FC, useEffect, useMemo, useState } from 'react'

import { streamingRestApi } from '@/service/streamingApi'
import {
  formatDGELicenseProperties,
  LicenseProperties,
  LicensesContent
} from '@/utils/api/licenseProperties'

import { Autocomplete } from '../Autocomplete'

type Props = {
  onChange: (value: number | null) => void
  value?: number
  exclude?: number[]
  disabled?: boolean
  error?: string
}

export const DGELicensePropertyAutocomplete: FC<Props> = ({
  onChange,
  exclude,
  value,
  disabled,
  error
}) => {
  const [properties, setProperties] = useState<LicenseProperties>([])

  useEffect(() => {
    const fetchData = async () => {
      const sports = await streamingRestApi.endpoints.sports.getSports()
      const { data: licenseProperties } =
        await streamingRestApi.endpoints.operators.getLicensesProperties()

      const data = await formatDGELicenseProperties(
        sports,
        licenseProperties.content as unknown as LicensesContent
      )
      setProperties(
        data
          .filter(
            (item) => !exclude?.some((excluded) => excluded.toString() === item.value.toString())
          )
          .sort((a, b) => {
            // sort initially by sport then by label alphabetically
            const sportComparison = (a?.sport ?? '').localeCompare(b?.sport ?? '')
            if (sportComparison !== 0) return sportComparison
            return a.label.localeCompare(b.label)
          })
      )
    }

    fetchData()
  }, [])

  const internalValue = useMemo(() => {
    const foundValue = properties.find((item) => item.value == value)
    return foundValue ? { label: foundValue.label, value: foundValue.value } : null
  }, [properties, value])

  return (
    <Autocomplete
      options={properties}
      value={internalValue}
      label="License Property"
      error={error}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => option.sport || ''}
      onChange={(value) => onChange(value ? Number(value.value) : null)}
      filterOptions={(options, state) =>
        options.filter((option) =>
          option.label.toLowerCase().includes(state.inputValue.toLowerCase())
        )
      }
    />
  )
}
